/* eslint-disable @typescript-eslint/no-explicit-any */
import { AgGridReact } from 'ag-grid-react'
import { ColDef, GridApi, GridOptions, IRowNode } from 'ag-grid-community'
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import { Button, Flex } from 'antd'
import { ClearOutlined, FileExcelOutlined } from '@ant-design/icons'

interface CustomAgGridProps<T> {
  // A number is treated as px. Pass a CSS string (e.g. '100%' or 'calc(100vh - 220px)')
  // to let the grid stretch — requires the ancestor chain to have a definite height.
  height?: number | string
  headerHeight?: number
  rowData?: T[] | null
  columnDefs: ColDef[]
  gridOptions?: GridOptions
  refetch?: () => void
  isLoading?: boolean
  toolbarExtra?: React.ReactNode
  allowExport?: boolean
}

export interface CustomAgGridRef<T> {
  getSelectedRows: () => T[]
  getAllRows: () => T[]
  getAllNodes: () => IRowNode<T>[]
  resetTableFilter: () => void
  api: GridApi<T> | undefined
}

export const CustomAgGrid = forwardRef(
  <T, >({
    height = 625,
    rowData,
    columnDefs,
    gridOptions,
    refetch,
    isLoading = false,
    toolbarExtra,
    allowExport = false,
  }: CustomAgGridProps<T>, ref: any) => {
    const gridRef = useRef<AgGridReact<T>>(null)

    const resetTableFilter = () => {
      gridRef.current?.api.setFilterModel(null)
      gridRef.current?.api.onFilterChanged()
    }

    useImperativeHandle(ref, () => ({
      getSelectedRows: () => {
        if (gridRef.current) {
          return gridRef.current.api.getSelectedRows()
        }
        return []
      },
      getAllRows: () => {
        if (gridRef.current) {
          const rowData: T[] = []
          gridRef.current.api.forEachNode((node) => {
            if (node.data) {
              rowData.push(node.data)
            }
          })
          return rowData
        }
        return []
      },
      getAllNodes: () => {
        if (gridRef.current) {
          const nodes: IRowNode<T>[] = []
          gridRef.current.api.forEachNode((node) => {
            if (node.data) {
              nodes.push(node)
            }
          })
          return nodes
        }
        return []
      },
      resetTableFilter: () => {
        if (gridRef.current) {
          resetTableFilter()
        }
      },
      api: gridRef.current?.api,
    }))

    const mergedGridOptions = useMemo<GridOptions>(() => {
      return {
        defaultColDef: {
          sortable: true,
          filter: true,
          resizable: true,
          flex: 1,
          minWidth: 150,
          enableRowGroup: true,
        },
        pivotMode: false,
        sideBar: {
          toolPanels: ['filters', 'columns'],
        },
        pagination: true,
        paginationPageSize: 20,
        paginationPageSizeSelector: [20, 50, 100],
        suppressContextMenu: true,
        rowSelection: {
          mode: 'multiRow',
          groupSelects: 'filteredDescendants',
          selectAll: 'filtered',
          headerCheckbox: true,
        },
        statusBar: {
          statusPanels: [
            { statusPanel: 'agTotalRowCountComponent' },
            { statusPanel: 'agFilteredRowCountComponent' },
            { statusPanel: 'agSelectedRowCountComponent' },
          ],
        },
        ...gridOptions,
        excelStyles: [
          {
            id: 'dateISO',
            dataType: 'DateTime',
            numberFormat: {
              format: 'yyy-mm-ddThh:mm:ss',
            },
          },
        ],
      }
    }, [gridOptions])

    return (
      <Flex vertical gap="small" className="ag-theme-quartz" style={{ height: height, width: '100%' }}>
        <Flex justify="space-between">
          <Flex gap="small">
            {refetch ? <Button onClick={refetch} loading={isLoading}>Refresh</Button> : null}
            <Button
              onClick={resetTableFilter}
              htmlType="button"
              icon={<ClearOutlined />}
            >
              Reset Filter
            </Button>
            {allowExport ?
              <Button
                onClick={() => gridRef.current?.api.exportDataAsExcel()}
                htmlType="button"
                icon={<FileExcelOutlined />}
              >
                Export
              </Button>
              : null
            }
          </Flex>
          {toolbarExtra}
        </Flex>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          loading={isLoading}
          gridOptions={mergedGridOptions}
        />
      </Flex>
    )
  }) as <T>(props: CustomAgGridProps<T> & { ref?: React.Ref<CustomAgGridRef<T>> }) => React.ReactNode
